import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { formatApiError } from './format-api-error';

export interface MutationToastMessages {
  loading: string;
  success: string;
  error: string;
}

export type MutationToastConfig = false | Partial<MutationToastMessages>;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(HotToastService);
  private readonly base = environment.apiUrl;

  get<T>(path: string): Observable<T> {
    return this.http.get<T>(`${this.base}${path}`, { withCredentials: true });
  }

  getBlob(path: string): Observable<Blob> {
    return this.http.get(`${this.base}${path}`, { withCredentials: true, responseType: 'blob' });
  }

  post<T>(path: string, body: unknown, feedback?: MutationToastConfig): Observable<T> {
    const request = this.http.post<T>(`${this.base}${path}`, body, { withCredentials: true });
    return this.withMutationToast(request, this.isUpload(body) ? 'upload' : 'save', feedback);
  }

  put<T>(path: string, body: unknown, feedback?: MutationToastConfig): Observable<T> {
    const request = this.http.put<T>(`${this.base}${path}`, body, { withCredentials: true });
    return this.withMutationToast(request, this.isUpload(body) ? 'upload' : 'save', feedback);
  }

  patch<T>(path: string, body: unknown, feedback?: MutationToastConfig): Observable<T> {
    const request = this.http.patch<T>(`${this.base}${path}`, body, { withCredentials: true });
    return this.withMutationToast(request, this.isUpload(body) ? 'upload' : 'save', feedback);
  }

  delete<T>(path: string, feedback?: MutationToastConfig): Observable<T> {
    const request = this.http.delete<T>(`${this.base}${path}`, { withCredentials: true });
    return this.withMutationToast(request, 'remove', feedback);
  }

  private withMutationToast<T>(
    request: Observable<T>,
    kind: 'save' | 'upload' | 'remove',
    feedback?: MutationToastConfig,
  ): Observable<T> {
    if (feedback === false) return request;

    const defaults = this.defaultMessages(kind);
    const messages = { ...defaults, ...feedback };
    return request.pipe(
      this.toast.observe<T, unknown>({
        loading: messages.loading,
        success: messages.success,
        error: (error) => formatApiError(error, messages.error),
      }),
    );
  }

  private defaultMessages(kind: 'save' | 'upload' | 'remove'): MutationToastMessages {
    if (kind === 'upload') {
      return {
        loading: 'Uploading…',
        success: 'Upload complete.',
        error: 'Upload failed.',
      };
    }
    if (kind === 'remove') {
      return {
        loading: 'Removing…',
        success: 'Removed successfully.',
        error: 'Could not remove the item.',
      };
    }
    return {
      loading: 'Saving…',
      success: 'Saved successfully.',
      error: 'Could not save changes.',
    };
  }

  private isUpload(body: unknown): boolean {
    return typeof FormData !== 'undefined' && body instanceof FormData;
  }
}
